import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../../components/common/Header';
import Button from '../../components/common/Button';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiCamera, FiZap, FiUsers, FiTrendingUp, FiGlobe, FiShield, 
  FiCalendar, FiEdit3, FiImage, FiLayers, FiSmartphone, FiCloudLightning
} = FiIcons;

const FeaturesPage = () => {
  const features = [
    {
      icon: FiCamera,
      title: 'Smart Gallery Management',
      description: 'AI-powered organization with facial recognition and scene detection for effortless photo management.',
      benefits: [
        'Automatic face detection and grouping',
        'Scene recognition for quick categorization',
        'Smart search with natural language processing',
        'Automated metadata tagging'
      ],
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      icon: FiZap,
      title: 'Advanced Photo Editing',
      description: 'Professional-grade editing tools with LUT support, custom templates, and batch processing.',
      benefits: [
        'LUT support for consistent color grading',
        'Customizable editing templates',
        'Non-destructive editing workflow',
        'Batch processing for multiple images'
      ],
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      icon: FiUsers,
      title: 'Client Collaboration',
      description: 'Seamless client proofing, feedback collection, and approval workflows with real-time notifications.',
      benefits: [
        'Client selection and favorites',
        'In-context commenting system',
        'Approval workflows with notifications',
        'Multiple decision-maker support'
      ],
      gradient: 'from-green-500 to-emerald-500'
    },
    {
      icon: FiTrendingUp,
      title: 'E-commerce Integration',
      description: 'Built-in store with zero commission, print lab integration, and automated order fulfillment.',
      benefits: [
        'Zero commission on digital sales',
        'Integrated print lab fulfillment',
        'Customizable pricing tiers',
        'Discount code management'
      ],
      gradient: 'from-orange-500 to-red-500'
    },
    {
      icon: FiGlobe,
      title: 'Global Reach',
      description: 'Multi-language support, CDN delivery, and localized payment methods for worldwide accessibility.',
      benefits: [
        'Support for 8+ languages',
        'Global CDN for fast loading',
        'Localized payment options',
        'Timezone-aware scheduling'
      ],
      gradient: 'from-indigo-500 to-purple-500'
    },
    {
      icon: FiShield,
      title: 'Enterprise Security',
      description: 'Advanced security features, watermarking, NSFW detection, and privacy controls.',
      benefits: [
        'Customizable watermarking',
        'NSFW content detection',
        'Granular privacy controls',
        'Two-factor authentication'
      ],
      gradient: 'from-teal-500 to-blue-500'
    }
  ];

  const additionalFeatures = [
    {
      icon: FiCalendar,
      title: 'Booking Management',
      description: 'Streamline your scheduling with integrated booking system, client reminders, and calendar integrations.'
    },
    {
      icon: FiEdit3,
      title: 'Website Builder',
      description: 'Create stunning portfolio websites with customizable templates, SEO optimization, and mobile responsiveness.'
    },
    {
      icon: FiImage,
      title: 'AI Image Enhancement',
      description: 'Leverage AI to automatically enhance image quality, fix lighting issues, and remove unwanted objects.'
    },
    {
      icon: FiLayers,
      title: 'Custom Branding',
      description: 'Apply your unique branding across galleries, emails, and client-facing interfaces for a consistent experience.'
    },
    {
      icon: FiSmartphone,
      title: 'Mobile Applications',
      description: 'Manage your photography business on the go with native iOS and Android applications.'
    },
    {
      icon: FiCloudLightning,
      title: 'Integration Ecosystem',
      description: 'Connect with your favorite tools through Zapier, webhooks, and our comprehensive API.'
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-dark-900">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-50 to-secondary-50 dark:from-primary-900/10 dark:to-secondary-900/10" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6"
          >
            Powerful Features for Modern Photographers
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto mb-8"
          >
            Everything you need to manage your photography business, from AI-powered organization to seamless client delivery.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex flex-wrap justify-center gap-4"
          >
            <Link to="/signup">
              <Button size="lg">Start Free Trial</Button>
            </Link>
            <Link to="/pricing">
              <Button variant="outline" size="lg">View Pricing</Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Main Features */}
      <section className="py-20 bg-white dark:bg-dark-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-24">
            {features.map((feature, index) => (
              <motion.div 
                key={feature.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
                className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 lg:gap-12 items-center`}
              >
                {/* Feature Image */}
                <div className="lg:w-1/2">
                  <div className={`bg-gradient-to-br ${feature.gradient} rounded-2xl p-8 aspect-video flex items-center justify-center`}>
                    <SafeIcon icon={feature.icon} className="w-24 h-24 text-white" />
                  </div>
                </div>

                {/* Feature Content */}
                <div className="lg:w-1/2">
                  <div className={`w-16 h-16 rounded-xl bg-gradient-to-r ${feature.gradient} flex items-center justify-center mb-6`}>
                    <SafeIcon icon={feature.icon} className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                    {feature.title}
                  </h2>
                  <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
                    {feature.description}
                  </p>
                  <ul className="space-y-3">
                    {feature.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-start">
                        <div className={`mt-1 w-5 h-5 rounded-full bg-gradient-to-r ${feature.gradient} flex items-center justify-center flex-shrink-0`}>
                          <SafeIcon icon={FiIcons.FiCheck} className="w-3 h-3 text-white" />
                        </div>
                        <span className="ml-3 text-gray-600 dark:text-gray-300">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Features */}
      <section className="py-20 bg-gray-50 dark:bg-dark-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Even More Powerful Features
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Discover the complete suite of tools designed to help photographers succeed in the digital age.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {additionalFeatures.map((feature, index) => (
              <motion.div 
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white dark:bg-dark-700 rounded-xl p-8 shadow-sm border border-gray-200 dark:border-gray-700"
              >
                <div className="w-12 h-12 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center mb-6">
                  <SafeIcon icon={feature.icon} className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-secondary-600 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Transform Your Photography Business?
          </h2>
          <p className="text-xl opacity-90 mb-8">
            Join thousands of photographers who have already made the switch to Tasveer.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/signup">
              <Button 
                variant="secondary" 
                size="xl" 
                className="bg-white text-primary-600 hover:bg-gray-100"
              >
                Start Free Trial
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                variant="outline" 
                size="xl" 
                className="border-white text-white hover:bg-white hover:text-primary-600"
              >
                Contact Sales
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FeaturesPage;